import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';
import { PublicFormCaptchaService } from './public-form-captcha.service';

@Component({
  standalone: true,
  selector: 'ain-public-form-captcha',
  template: `
    @if (provider() === 'turnstile' && siteKey()) {
      <div class="captcha-shell" [attr.aria-label]="t('dynamic.public.captchaLabel')">
        <div #turnstileHost class="turnstile-host"></div>
      </div>
    }
  `,
  styles: [`
    .captcha-shell { margin: 12px 0; }
    .turnstile-host { min-height: 65px; }
  `]
})
export class PublicFormCaptchaComponent implements AfterViewInit, OnDestroy {
  private readonly captchaService = inject(PublicFormCaptchaService);
  private readonly localization = inject(LocalizationService);

  readonly provider = input<string>('off');
  readonly siteKey = input<string | null | undefined>(null);

  readonly tokenChange = output<string | null>();

  private readonly turnstileHost = viewChild<ElementRef<HTMLElement>>('turnstileHost');
  private widgetId: string | null = null;

  ngAfterViewInit(): void {
    this.mountWidget();
  }

  ngOnDestroy(): void {
    if (this.widgetId) {
      this.captchaService.removeTurnstile(this.widgetId);
      this.widgetId = null;
    }
  }

  reset(): void {
    this.tokenChange.emit(null);
    if (this.widgetId) {
      this.captchaService.resetTurnstile(this.widgetId);
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private mountWidget(): void {
    if (this.provider() !== 'turnstile' || !this.siteKey()) {
      return;
    }

    const host = this.turnstileHost()?.nativeElement;
    if (!host) {
      return;
    }

    void this.captchaService.renderTurnstile(host, this.siteKey()!, {
      onToken: token => this.tokenChange.emit(token),
      onExpired: () => this.tokenChange.emit(null),
      onError: () => this.tokenChange.emit(null)
    }).then(widgetId => {
      this.widgetId = widgetId;
    }).catch(() => this.tokenChange.emit(null));
  }
}
